import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Clock, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { allPrestations } from "@/data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-[#454C50] focus:border-[#6EB000] outline-none px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors duration-300";

const initial = { nom: "", telephone: "", email: "", adresse: "", code_postal: "", ville: "", vehicule: "", prestation: "Stage 1", date: "", time: "" };

const HORAIRES = [
  { day: "Lundi — Vendredi", hours: "9h00 – 19h00", open: true },
  { day: "Samedi", hours: "9h00 – 13h00", note: "un samedi sur deux – sur rendez-vous", open: true },
  { day: "Dimanche", hours: "Fermé", open: false },
];

export default function Reservation() {
  const [form, setForm] = useState(initial);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "Réservation — NBPerf";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickDate = async (e) => {
    const date = e.target.value;
    setForm((f) => ({ ...f, date, time: "" }));
    setSlots(null);
    if (!date) return;
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${API}/bookings/slots`, { params: { date } });
      setSlots(r.data);
    } catch {
      toast.error("Impossible de charger les créneaux.");
    } finally {
      setLoadingSlots(false);
    }
  };

  const valid =
    form.nom && form.telephone && form.email && form.adresse && form.code_postal &&
    form.ville && form.vehicule && form.prestation && form.date && form.time;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Complétez tous les champs et choisissez un créneau.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookings`, form);
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Ce créneau vient d'être réservé. Choisissez-en un autre.");
        pickDate({ target: { value: form.date } });
        setForm((f) => ({ ...f, time: "" }));
      } else {
        toast.error(err.response?.data?.detail || "Erreur lors de la réservation.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="pt-16 min-h-screen" data-testid="reservation-done">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20 text-center">
          <CheckCircle2 className="w-16 h-16 text-[#6EB000] mx-auto" strokeWidth={1.4} />
          <h1 className="font-display text-4xl sm:text-6xl text-white mt-8" data-testid="reservation-done-title">Rendez-vous enregistré</h1>
          <p className="text-white/60 mt-4">
            Votre demande est confirmée pour le{" "}
            <span className="text-[#6EB000] font-bold">
              {new Date(done.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {done.time}
            </span>
            . Un email de confirmation vient de vous être envoyé, et Nicolas vous recontacte pour valider le lieu exact.
          </p>
          <div className="mt-10 bg-[#343A3D] border border-[#454C50] p-6 text-left space-y-1" data-testid="reservation-recap">
            <p className="text-sm text-white/80">{done.prestation}</p>
            <p className="text-sm text-white/50">{done.vehicule}</p>
            <p className="font-mono text-[#6EB000] text-xs mt-2">Réf. {done.booking_id}</p>
          </div>
          <Link to="/" className="mt-10 inline-flex items-center gap-2 text-[#6EB000] text-sm uppercase tracking-widest hover:underline" data-testid="reservation-back-home">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>
        </div>
      </main>
    );
  }

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date(Date.now() + 90 * 86400000).toISOString().split("T")[0];

  return (
    <main className="pt-16" data-testid="reservation-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="font-script text-2xl text-[#6EB000] mb-2">Service de proximité, sur rendez-vous</p>
        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl text-white" data-testid="reservation-title">
          Réservez votre <span className="text-[#6EB000]">créneau</span>
        </h1>

        <div className="grid lg:grid-cols-3 gap-10 mt-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="bg-[#343A3D] border border-[#454C50] p-7 h-fit"
            data-testid="horaires-panel"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
              <h2 className="font-display text-2xl text-white">Horaires</h2>
            </div>
            <div className="space-y-4">
              {HORAIRES.map((h) => (
                <div key={h.day} className="border-b border-[#454C50] pb-4 last:border-0">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/70">{h.day}</span>
                    <span className={`font-display ${h.open ? "text-[#6EB000]" : "text-white/30"}`}>{h.hours}</span>
                  </div>
                  {h.note && <p className="font-script text-lg text-[#F5A623] mt-1">{h.note}</p>}
                </div>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-6 leading-relaxed">
              Les créneaux déjà réservés n'apparaissent plus comme disponibles. Intervention à l'atelier ou à domicile
              (forfait déplacement selon distance).
            </p>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-2 space-y-5"
            data-testid="reservation-form"
          >
            <div className="flex items-center gap-3 mb-2">
              <CalendarClock className="w-6 h-6 text-[#6EB000]" strokeWidth={1.6} />
              <h2 className="font-display text-2xl text-white">Votre rendez-vous</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Nom et prénom *" value={form.nom} onChange={set("nom")} className={inputCls} data-testid="resa-nom" />
              <input required placeholder="Téléphone *" value={form.telephone} onChange={set("telephone")} className={inputCls} data-testid="resa-telephone" />
              <input required type="email" placeholder="E-mail *" value={form.email} onChange={set("email")} className={inputCls} data-testid="resa-email" />
              <input required placeholder="Véhicule * (ex : Golf 7 1.4 TSI 125ch)" value={form.vehicule} onChange={set("vehicule")} className={inputCls} data-testid="resa-vehicule" />
              <input required placeholder="Adresse *" value={form.adresse} onChange={set("adresse")} className={`${inputCls} sm:col-span-2`} data-testid="resa-adresse" />
              <input required placeholder="Code postal *" value={form.code_postal} onChange={set("code_postal")} className={inputCls} data-testid="resa-code-postal" />
              <input required placeholder="Ville *" value={form.ville} onChange={set("ville")} className={inputCls} data-testid="resa-ville" />
              <select value={form.prestation} onChange={set("prestation")} className={`${inputCls} bg-[#272B2E] sm:col-span-2`} data-testid="resa-prestation">
                {allPrestations.map((p) => (
                  <option key={p.id} value={p.label}>{p.label}</option>
                ))}
                <option value="Autre demande">Autre demande</option>
              </select>
            </div>

            <div>
              <label htmlFor="resa-date" className="block text-[11px] uppercase tracking-widest text-white/40 mb-2">Date souhaitée *</label>
              <input
                id="resa-date"
                type="date"
                min={today}
                max={maxDate}
                value={form.date}
                onChange={pickDate}
                className={`${inputCls} bg-[#272B2E] [color-scheme:dark]`}
                data-testid="resa-date"
              />
            </div>

            {loadingSlots && (
              <p className="flex items-center gap-2 text-white/50 text-sm" data-testid="resa-slots-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement des créneaux…
              </p>
            )}
            {slots && !slots.open && (
              <p className="text-[#F5A623] text-sm border border-[#F5A623]/40 bg-[#F5A623]/5 p-4" data-testid="resa-closed-note">
                Fermé ce jour-là (dimanche, ou samedi non travaillé). Choisissez une autre date — rappel : {slots.note}.
              </p>
            )}
            {slots?.open && (
              <div data-testid="resa-slots">
                <p className="text-[11px] uppercase tracking-widest text-white/40 mb-3">Créneaux disponibles</p>
                <div className="flex flex-wrap gap-2">
                  {slots.slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setForm((f) => ({ ...f, time: s.time }))}
                      data-testid={`resa-slot-${s.time.replace(":", "h")}`}
                      className={`px-4 py-2.5 text-sm font-bold border transition-colors duration-200 ${
                        !s.available
                          ? "border-[#454C50] text-white/20 line-through cursor-not-allowed"
                          : form.time === s.time
                            ? "border-[#6EB000] bg-[#6EB000] text-black"
                            : "border-[#454C50] text-white/70 hover:border-[#6EB000] hover:text-[#6EB000]"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !form.time}
              className="clip-angle inline-flex items-center gap-2 bg-[#6EB000] text-black font-bold px-8 py-5 text-sm uppercase tracking-widest hover:bg-[#5A9000] active:scale-95 transition-all duration-200 disabled:opacity-50"
              data-testid="resa-submit-button"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirmer mon rendez-vous
            </button>

            <div className="border border-[#454C50] p-5 mt-2" data-testid="resa-payment-trust">
              <p className="text-[11px] uppercase tracking-widest text-white/40 mb-3">Moyens de paiement acceptés</p>
              <ul className="grid sm:grid-cols-2 gap-2 text-xs text-white/70">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement sécurisé par PayPal</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Carte bancaire acceptée</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement en plusieurs fois disponible via PayPal, sous réserve d'éligibilité</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#6EB000] shrink-0 mt-0.5" /> Paiement en espèces possible sur place</li>
              </ul>
            </div>
          </motion.form>
        </div>
      </div>
    </main>
  );
}
